import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  CircularProgress,
  Typography,
  Alert,
  Container,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { authCookieService } from '../services/authCookies';
import { logger } from '../utils/logger';
import OnboardingModal from '../components/OnboardingModal';

/**
 * Auth Callback Page
 *
 * Handles the redirect from backend after successful Google OAuth.
 * Backend has already set httpOnly cookies, we just need to:
 * 1. Check auth status
 * 2. Fetch user data
 * 3. Redirect to appropriate page
 */
const AuthCallbackPage: React.FC = () => {
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(true);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { updateUser, landingFormData, showOnboarding, setShowOnboarding } = useAuth();

  useEffect(() => {
    const handleCallback = async () => {
      try {
        // Check for error param
        const errorParam = searchParams.get('error');
        if (errorParam) {
          setError('Authentication failed. Please try again.');
          setIsProcessing(false);
          return;
        }

        // Check for token param (new OAuth flow)
        const token = searchParams.get('token');
        const success = searchParams.get('success');
        const oauth = searchParams.get('oauth');
        const inactive = searchParams.get('inactive');

        let authenticatedUser = null;

        // Handle new token exchange flow
        if (token) {
          logger.info('Exchanging OAuth token for auth cookies');

          try {
            // Exchange the token for auth cookies
            const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3000'}/api/v1/auth/exchange`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              credentials: 'include', // Important for setting cookies
              body: JSON.stringify({ token })
            });

            if (!response.ok) {
              const contentType = response.headers.get('content-type');
              let errorData: { error?: string } | null = null;

              try {
                if (contentType && contentType.includes('application/json')) {
                  errorData = await response.json();
                } else {
                  const message = await response.text();
                  errorData = { error: message };
                }
              } catch (parseError) {
                logger.warn('Failed to parse exchange error response', parseError);
              }

              logger.error('Token exchange failed:', errorData || response.statusText);
              setError(
                errorData?.error?.trim() ||
                (response.status === 429 ? 'Too many attempts. Please wait a moment before trying again.' : 'Failed to authenticate. Please try logging in again.')
              );
              setIsProcessing(false);
              return;
            }

            const data = await response.json();
            authenticatedUser = data.user;

            if (!authenticatedUser) {
              setError('Failed to get user data. Please try logging in again.');
              setIsProcessing(false);
              return;
            }

            // Store user data and update auth context
            authCookieService.handleAuthSuccess(authenticatedUser);
            updateUser(authenticatedUser);

            // Handle inactive user case (user without organization or deactivated)
            if (data.inactiveUser) {
              logger.info('Inactive user detected from exchange');

              // Check if this is a new user needing onboarding (no organization)
              if (!authenticatedUser.organizationId) {
                logger.info('User needs onboarding (no organization)');
                setShowOnboarding(true);
                // Don't navigate to dashboard - stay on current page with modal
                return;
              }

              // Otherwise it's a deactivated user - let them proceed to see inactive message
              logger.info('User is deactivated');
              navigate('/dashboard');
              return;
            }

            // Handle new user case (backward compatibility)
            if (data.isNewUser) {
              logger.info('New user detected, showing onboarding modal');
              setShowOnboarding(true);
              // Don't navigate to dashboard for new users
              return;
            }

          } catch (exchangeError) {
            logger.error('Token exchange error:', exchangeError);
            setError('Failed to complete authentication. Please try logging in again.');
            setIsProcessing(false);
            return;
          }
        }
        // Handle legacy flow (backward compatibility)
        else if (success === 'true' || oauth === 'true') {
          // Check authentication status (cookies were set by backend)
          const { isAuthenticated, user } = await authCookieService.checkAuthStatus();

          if (!isAuthenticated || !user) {
            setError('Failed to authenticate. Please try logging in again.');
            setIsProcessing(false);
            return;
          }

          authenticatedUser = user;

          // Store user data and update auth context
          authCookieService.handleAuthSuccess(authenticatedUser);
          updateUser(authenticatedUser);

          // Handle inactive user case
          // Only trust backend's inactive flag, not organizationId
          if (inactive === 'true') {
            logger.info('Inactive user detected, showing onboarding modal');
            setShowOnboarding(true);
            navigate('/dashboard'); // Navigate to dashboard where modal will appear
            return;
          }
        } else {
          setError('Invalid callback parameters.');
          setIsProcessing(false);
          return;
        }

        // Determine redirect destination
        let redirectTo = '/dashboard';

        // Check if there's pending landing form data
        if (landingFormData) {
          if (landingFormData.mode === 'simulation') {
            redirectTo = '/simulations/create';
          } else if (landingFormData.mode === 'ideas') {
            redirectTo = '/ideas/create';
          }
        }

        // Check if there's a stored redirect path
        const storedRedirect = sessionStorage.getItem('auth_redirect_path');
        if (storedRedirect) {
          redirectTo = storedRedirect;
          sessionStorage.removeItem('auth_redirect_path');
        }

        logger.info('Auth callback successful, redirecting to:', redirectTo);
        navigate(redirectTo);
      } catch (err) {
        logger.error('Auth callback error:', err);
        setError('An unexpected error occurred. Please try logging in again.');
        setIsProcessing(false);
      }
    };

    handleCallback();
  }, [searchParams, navigate, updateUser, landingFormData, setShowOnboarding]);

  if (error) {
    return (
      <Container maxWidth="sm">
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            textAlign: 'center',
          }}
        >
          <Alert severity="error" sx={{ mb: 2, width: '100%' }}>
            {error}
          </Alert>
          <Typography variant="body2" sx={{ mt: 2 }}>
            <a href="/login">Return to login</a>
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <>
      {/* Show OnboardingModal when needed */}
      {showOnboarding && (
        <OnboardingModal
          isOpen={showOnboarding}
          onClose={() => setShowOnboarding(false)}
          expressMode={!!landingFormData}
          landingFormData={landingFormData}
        />
      )}

      <Container maxWidth="sm">
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            textAlign: 'center',
          }}
        >
          {showOnboarding ? (
            <>
              <Typography variant="h5" gutterBottom>
                Setting up your workspace...
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Please complete the onboarding process
              </Typography>
            </>
          ) : (
            <>
              <CircularProgress size={60} sx={{ mb: 3 }} />
              <Typography variant="h5" gutterBottom>
                Completing sign in...
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Please wait while we set up your account
              </Typography>
            </>
          )}
        </Box>
      </Container>
    </>
  );
};

export default AuthCallbackPage;
